import { useSearchParams } from 'react-router-dom';

import { stripAutoscalingFiltersFromApiFilters } from '~/shared/utils/autoscalingFilterUtils';
import {
  apiFiltersParamKey,
  clearAllFilterSearchParams,
} from '~/shared/utils/filterUrlParams';
import { safeJsonParseArray } from '~/shared/utils/safeJson';

import { FilterStoredInURL, buildAutoscalingApiFilters } from '../filterUtils';
import { buildFinalApiFilters, writeFilterParams } from '../filterUrlParams';
import type { VolumeFilterPanelState } from '../panelState';

export function useVolumeFilterUrlParams() {
  const [_searchParams, setSearchParams] = useSearchParams();

  const applyFilters = (pending: VolumeFilterPanelState) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        // Tolerant parse, matching `appliedFilters.ts`: a hand-edited or truncated `api_filters`
        // must not throw out of this updater and take the click handler down with it.
        const existingFiltersFromUrl = safeJsonParseArray<FilterStoredInURL>(
          prevParams.get(apiFiltersParamKey)
        );

        const finalApiFilters = buildFinalApiFilters(existingFiltersFromUrl, pending);

        writeFilterParams(
          updatedParams,
          finalApiFilters,
          pending.autoscaleState,
          pending.eligibility
        );

        return updatedParams;
      },
      { replace: true }
    );
  };

  const applyAutoscalingFiltersOnly = (stateIds: string[], eligibilityIds: string[]) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        // Tolerant parse, matching `appliedFilters.ts`: a hand-edited or truncated `api_filters`
        // must not throw out of this updater and take the click handler down with it.
        const existingFiltersFromUrl = safeJsonParseArray<FilterStoredInURL>(
          prevParams.get(apiFiltersParamKey)
        );

        const filtersWithoutAutoscaling =
          stripAutoscalingFiltersFromApiFilters(existingFiltersFromUrl);
        const autoscalingApiFilters = buildAutoscalingApiFilters(stateIds, eligibilityIds);
        const finalApiFilters = [
          ...filtersWithoutAutoscaling,
          ...autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
          ...autoscalingApiFilters.filter((f) => f.group === 'supported'),
        ];

        writeFilterParams(updatedParams, finalApiFilters, stateIds, eligibilityIds);

        return updatedParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const updatedParams = new URLSearchParams(prev);
        clearAllFilterSearchParams(updatedParams, apiFiltersParamKey);
        return updatedParams;
      },
      { replace: true }
    );
  };

  return { applyFilters, applyAutoscalingFiltersOnly, resetFilters };
}
